import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { SchemaOrg } from '@/components/base/SchemaOrg';
import { aboutContent, siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const { brandIntro, reputation, contact } = aboutContent;
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const websiteAlt = formData.get('website_alt') as string;
    if (websiteAlt && websiteAlt.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9murqcit35ii3jdlmeg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试');
    }
  };

  return (
    <>
      <SchemaOrg
        type="WebPage"
        pageTitle="关于我们 - 八戒影院"
        pageDescription="八戒影院成立于2019年，是国内领先的在线影视平台。了解八戒影院的品牌故事、口碑评价和联系方式。"
        pageUrl="/about"
      />
      <SchemaOrg type="Organization" />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24 pb-8">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]} />
        </div>

        <section className="w-full px-4 md:px-6 mt-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
            {brandIntro.title}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {brandIntro.subtitle}
          </p>
          <div className="mt-6 space-y-4 text-sm text-foreground-600 leading-relaxed max-w-4xl">
            {brandIntro.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 mt-12 md:mt-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center">
            {reputation.title}
          </h2>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {reputation.stats.map((stat, i) => (
              <div key={i} className="text-center p-5 rounded-lg bg-background-100 border border-background-300/40">
                <p className="text-2xl md:text-3xl font-heading font-black text-primary-500">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {reputation.testimonials.map((t, i) => (
              <div key={i} className="p-5 rounded-lg bg-background-100 border border-background-300/40">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-primary-500 text-sm" />
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                <div className="mt-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary-500">{t.name[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="w-full px-4 md:px-6 mt-12 md:mt-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center">
            {contact.title}
          </h2>
          <p className="mt-2 text-sm text-foreground-600 text-center">
            {contact.subtitle}
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {contact.info.map((item, i) => (
              <div key={i} className="p-4 rounded-lg bg-background-100 border border-background-300/40 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-primary-500 text-lg`} />
                </div>
                <div>
                  <p className="text-xs text-foreground-500">{item.label}</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-lg mx-auto">
            <h3 className="text-lg font-heading font-bold text-foreground-800 text-center mb-5">
              在线留言
            </h3>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col gap-4"
            >
              <div className="form-website-alt-wrapper">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <input
                type="text"
                name="name"
                placeholder="您的姓名"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/40 text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
              <input
                type="email"
                name="email"
                placeholder="您的邮箱"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/40 text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
              <textarea
                name="message"
                placeholder="请输入您的留言（最多500字）"
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/40 text-sm text-foreground-800 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
              />
              <button
                type="submit"
                className="w-full py-2.5 bg-primary-500 text-background-950 font-semibold rounded-full text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-center text-primary-400">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-center text-accent-400">{formMessage}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}